import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import emailjs from "@emailjs/browser";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  InputBase,
  Tabs,
  Tab,
  Slide,
  Button,
  TextField,
  useMediaQuery,
  useTheme,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
} from "@mui/material";

/* ══════════════════════════════════════════
   EMAILJS CONFIG
══════════════════════════════════════════ */
const EMAILJS_CONFIG = {
  SERVICE_ID: "service_rli6cks",
  TEMPLATE_ID: "template_604aimz",
  PUBLIC_KEY: "I49rHos2Ys_lPJ_zx",
};

/* ══════════════════════════════════════════
   PORTFOLIO CONFIG
══════════════════════════════════════════ */
const CONFIG = {
  name: "Madhu's Assistant",
  role: "Mechanical Design Engineer",
  primary: "#1a3050",
  steel: "#457b9d",
  accent: "#457b9d",
  email: "vangurimadhu13@gmail.com",
  linkedin: "linkedin.com/in/madhuvanguri",
  location: "Hyderabad, India",
};

/* ══════════════════════════════════════════
   GLASS DESIGN TOKENS
══════════════════════════════════════════ */
const G = {
  bg: "#f4f3ef",
  glass: "rgba(255, 255, 255, 0.72)",
  glassDeep: "rgba(255, 255, 255, 0.88)",
  glassDim: "rgba(255, 255, 255, 0.45)",
  border: "rgba(26, 48, 80, 0.10)",
  borderHi: "rgba(255, 255, 255, 0.65)",
  ink: "#1a1c22",
  ink2: "#2f3542",
  muted: "#7a8499",
  primary: "#1a3050",
  steel: "#457b9d",
  mono: "'IBM Plex Mono', monospace",
  display: "'Cormorant Garamond', Georgia, serif",
  body: "'Figtree', sans-serif",
};

/* shared glass style helper */
const glassPanel = (extra = {}) => ({
  background: G.glass,
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: `1px solid ${G.border}`,
  borderRadius: 12,
  boxShadow:
    "0 4px 32px rgba(26,48,80,0.09), 0 1px 4px rgba(26,48,80,0.05)",
  position: "relative",
  overflow: "hidden",
  ...extra,
});

/* ══════════════════════════════════════════
   DATA
══════════════════════════════════════════ */
const PROJECTS = [
  {
    id: 1,
    title: "X-Ray Monoblock Assembly",
    role: "RayMedis",
    software: "SolidWorks · Thermal FEA",
    category: "Medical Systems",
    desc:
      "Mechanical housing integrating radiation shielding and anode heatsink features.",
    link3D:
      "/cad-work/project3/project3.html?model=MonoBlock&title=MonoBlock+Generator+Assembly",
    link2D:
      "/cad-work/four-views.html?image=/cad-work/project3/view3.png&title=MonoBlock+Layout",
  },
  {
    id: 2,
    title: "X-Ray Chest Stand",
    role: "RayMedis",
    software: "SolidWorks · Sheet Metal",
    category: "Mechanisms",
    desc:
      "Floor-mounted stand with dynamic height and positioning adjustment mechanisms.",
    link3D:
      "/cad-work/project1/project1.html?model=Chest-Stand&title=Chest+Stand+Assembly",
    link2D:
      "/cad-work/four-views.html?image=/cad-work/project1/view1.png&title=Chest+Stand+Layout",
  },
  {
    id: 3,
    title: "X-Ray Tube Assembly",
    role: "RayMedis",
    software: "SolidWorks · GD&T",
    category: "Thermal Design",
    desc:
      "Precision structural packaging to manage extreme X-Ray production temperatures.",
    link3D:
      "/cad-work/project2/project2.html?model=x-ray-tube&title=X-Ray+Tube+Assembly",
    link2D:
      "/cad-work/four-views.html?image=/cad-work/project2/view2.png&title=X-Ray+Layout",
  },
  {
    id: 4,
    title: "IoT-Based Robotic Stretcher",
    role: "Academic",
    software: "SolidWorks · IoT",
    category: "Mechanisms",
    desc:
      "Kinematics and electro-mechanical integration for healthcare logistics.",
    link3D: "/cad-work/robotic-stretcher-3d.html",
    link2D: "/cad-work/robotic-stretcher-drawing.pdf#toolbar=0",
  },
  {
    id: 5,
    title: "Precision Lathe Part",
    role: "DUN Tech",
    software: "Machining",
    category: "Manufacturing",
    desc:
      "High-precision turning component manufactured to ±0.01 mm tolerance.",
    link3D: "/cad-work/lathe-machine-3d.html",
    link2D: "/cad-work/lathe-machine-drawing.pdf#toolbar=0",
  },
];

const RESUME_DATA = {
  experience: [
    {
      role: "Mechanical Design Engineer",
      company: "RayMedis Medical Technologies Pvt. Ltd.",
      date: "May 2025 – Present",
      desc:
        "Designing medical imaging systems (X-Ray Monoblock, Chest Stands). Applying SolidWorks, GD&T, thermal management, and DFM/DFA.",
    },
    {
      role: "Mechanical Design Intern",
      company: "Student Tribe",
      date: "Mar 2025 – May 2025",
      desc:
        "Reduced prototype weight by 15% via geometry optimisation. Used ANSYS for structural validation.",
    },
    {
      role: "Lathe Machine Operator Intern",
      company: "DUN Technologies",
      date: "Jan 2024 – Feb 2024",
      desc:
        "Machined precision components to ±0.01 mm tolerance. Reduced cycle time by 15%.",
    },
  ],
};

/* ══════════════════════════════════════════
   ICONS
══════════════════════════════════════════ */
const Icons = {
  Send: ({ color }) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      width="18"
      height="18"
    >
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  ),

  Attach: ({ color }) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      width="18"
      height="18"
    >
      <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
    </svg>
  ),

  Cube: ({ color }) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      width="14"
      height="14"
    >
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),

  Draft: ({ color }) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      width="14"
      height="14"
    >
      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
    </svg>
  ),

  Clear: ({ color }) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      width="16"
      height="16"
    >
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  ),

  Close: ({ color }) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      width="16"
      height="16"
    >
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  ),
};

/* ══════════════════════════════════════════
   TYPING DOTS
══════════════════════════════════════════ */
const TypingDots = memo(() => (
  <Box
    sx={{
      display: "flex",
      gap: "5px",
      alignItems: "center",
      p: "10px 14px",
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: G.steel,
          animation:
            "botBounce 1.2s ease-in-out infinite",
          animationDelay: `${i * 0.18}s`,
          "@keyframes botBounce": {
            "0%,80%,100%": {
              transform: "scale(0.5)",
              opacity: 0.35,
            },
            "40%": {
              transform: "scale(1)",
              opacity: 1,
            },
          },
        }}
      />
    ))}
  </Box>
));

TypingDots.displayName = "TypingDots";

/* ══════════════════════════════════════════
   MARKDOWN-LITE RENDERER
══════════════════════════════════════════ */
const MessageText = memo(({ text }) => {
  const lines = text.split("\n");

  return (
    <Box>
      {lines.map((line, i) => {
        if (
          line.startsWith("- ") ||
          line.startsWith("• ")
        ) {
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: 0.8,
                mb: 0.3,
              }}
            >
              <span
                style={{
                  color: G.steel,
                  flexShrink: 0,
                  marginTop: 2,
                }}
                aria-hidden="true"
              >
                ▸
              </span>

              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: G.ink2,
                  lineHeight: 1.65,
                  fontSize: "0.84rem",
                }}
                dangerouslySetInnerHTML={{
                  __html: line
                    .replace(/^[-•]\s/, "")
                    .replace(
                      /\*\*(.*?)\*\*/g,
                      "<strong>$1</strong>"
                    ),
                }}
              />
            </Box>
          );
        }

        if (line.match(/^\d+\.\s/)) {
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: 0.8,
                mb: 0.3,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: G.steel,
                  flexShrink: 0,
                  fontWeight: 600,
                  fontSize: "0.82rem",
                }}
              >
                {line.match(/^\d+/)[0]}.
              </Typography>

              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: G.ink2,
                  lineHeight: 1.65,
                  fontSize: "0.84rem",
                }}
                dangerouslySetInnerHTML={{
                  __html: line
                    .replace(/^\d+\.\s/, "")
                    .replace(
                      /\*\*(.*?)\*\*/g,
                      "<strong>$1</strong>"
                    ),
                }}
              />
            </Box>
          );
        }

        if (line.trim() === "") {
          return (
            <Box
              key={i}
              sx={{ height: 5 }}
            />
          );
        }

        return (
          <Typography
            key={i}
            variant="body2"
            sx={{
              color: G.ink2,
              lineHeight: 1.7,
              mb: 0.2,
              fontSize: "0.84rem",
            }}
            dangerouslySetInnerHTML={{
              __html: line.replace(
                /\*\*(.*?)\*\*/g,
                "<strong>$1</strong>"
              ),
            }}
          />
        );
      })}
    </Box>
  );
});

MessageText.displayName = "MessageText";

const Transition = React.forwardRef(
  (props, ref) => (
    <Slide
      direction="up"
      ref={ref}
      {...props}
    />
  )
);

Transition.displayName = "Transition";

/* ══════════════════════════════════════════
   SUGGESTIONS
══════════════════════════════════════════ */
const SUGGESTION_SETS = {
  default: [
    "What projects has Madhu worked on?",
    "SolidWorks experience?",
    "Is he open to hire?",
    "Explain GD&T",
  ],

  after_project: [
    "View 3D Models",
    "What is DFM?",
    "Notice period?",
    "Download Résumé",
  ],

  after_contact: [
    "Expected CTC?",
    "Available immediately?",
    "LinkedIn profile?",
    "Key strengths?",
  ],

  after_resume: [
    "Tell me about RayMedis",
    "Top skills?",
    "Contact Madhu",
    "See 3D Projects",
  ],

  technical: [
    "What is GD&T?",
    "Explain Tolerance Stackup",
    "What is FEA?",
    "DFM vs DFA?",
  ],

  recruiter: [
    "Notice period?",
    "Expected CTC?",
    "Open for relocation?",
    "Schedule interview?",
  ],
};

/* ══════════════════════════════════════════
   KNOWLEDGE BASE
══════════════════════════════════════════ */
const KNOWLEDGE_BASE = [
  {
    keys: [
      "hi",
      "hii",
      "hiii",
      "hello",
      "hlo",
      "hey",
      "yo",
      "sup",
      "what's up",
      "wassup",
      "hy",
      "hru",
      "good morning",
      "good afternoon",
      "good evening",
      "how are you",
      "bro",
      "dude",
      "bruh",
    ],
    text:
      "Hello! I'm Madhu's portfolio assistant. I have full knowledge of his engineering experience, projects, and availability. What would you like to know?",
  },

  {
    keys: [
      "lol",
      "haha",
      "hehe",
      "hmm",
      "ok",
      "okay",
      "kk",
      "cool",
      "nice",
      "wow",
      "test",
      "testing",
      "ping",
      "echo",
      "asdf",
      "qwerty",
      "random",
      "blah",
    ],
    text:
      "All systems nominal. What would you like to know about Madhu's engineering background?",
  },

  {
    keys: [
      "what is your name",
      "what's your name",
      "who are you",
      "who made you",
      "are you ai",
      "are you a robot",
      "is this chatbot",
      "human or bot",
      "did madhu create you",
    ],
    text:
      "I'm a custom portfolio assistant built by Madhu Vanguri — a local logic engine containing his full résumé, skill set, and project data.",
  },

  {
    keys: [
      "what can you do",
      "help menu",
      "what can i ask",
      "how does this work",
      "your abilities",
    ],
    text:
      "Ask me about Madhu's projects ('Show projects'), skills ('SolidWorks experience'), career ('Notice period?'), or technical concepts ('What is GD&T?').",
  },

  {
    keys: [
      "who is madhu",
      "about madhu",
      "tell me about madhu",
      "whose portfolio is this",
      "introduce yourself",
      "describe yourself",
    ],
    text:
      "Madhu Vanguri is a Mechanical Design Engineer specialising in medical device design, thermal management, and complex precision assemblies for X-Ray imaging systems at RayMedis Medical Technologies.",
  },

  {
    keys: [
      "where are you from",
      "location",
      "current city",
      "hyderabad",
      "from telangana",
      "work location",
      "city",
    ],
    text:
      "Madhu is based in Hyderabad, Telangana, India — and is open to relocating to Bangalore, Pune, or Chennai for the right role.",
  },

  {
    keys: [
      "hobby",
      "hobbies",
      "free time",
      "interests",
      "passion",
      "trekking",
      "volleyball",
    ],
    text:
      "Madhu's engineering side project passion is rapid prototyping. Outside the office: trekking, volleyball, and automotive R&D.",
  },

  {
    keys: [
      "qualification",
      "education",
      "degree",
      "college",
      "btech",
      "mechanical engineer",
      "where did madhu study",
    ],
    text:
      "Madhu holds a B.E. in Mechanical Engineering from TKR College of Engineering and Technology, Hyderabad (2020–2024).",
  },

  {
    keys: [
      "where does madhu work",
      "current company",
      "current job",
      "work experience",
      "years of experience",
      "current role",
      "raymedis",
      "medical devices",
    ],
    text:
      "Madhu is currently a Mechanical Design Engineer at RayMedis Medical Technologies (May 2025 – Present), focused on X-Ray imaging system design. Prior internships at Student Tribe and DUN Technologies.",
  },

  {
    keys: [
      "skills",
      "technical skills",
      "expertise",
      "cad skills",
      "solidworks",
      "autocad",
      "catia",
      "ansys",
    ],
    text:
      "Core: SolidWorks (Advanced), AutoCAD, CATIA V5, ANSYS FEA/Thermal, GD&T (ASME Y14.5), Tolerance Stack-Up, DFM/DFA, Sheet Metal Design.",
  },

  {
    keys: [
      "gd&t",
      "geometric",
      "tolerance stackup",
      "tolerance",
      "stack",
      "rss",
    ],
    text:
      "Madhu rigorously applies GD&T (ASME Y14.5), using RSS and Worst-Case Stackup analysis to ensure complex multi-part medical assemblies meet functional requirements without interference.",
  },

  {
    keys: [
      "dfm",
      "dfa",
      "manufacturing",
      "machining",
      "lathe",
      "sheet metal",
    ],
    text:
      "Madhu has hands-on machining experience (Lathe Operator Intern) — meaning he designs with DFM/DFA knowing exactly how parts are physically made and assembled.",
  },

  {
    keys: [
      "ansys",
      "fea",
      "simulation",
      "design validation",
      "structural",
      "thermal",
    ],
    text:
      "He uses ANSYS Workbench for Structural & Thermal FEA. Examples include validating heat dissipation geometries for high-temperature X-Ray tube assemblies.",
  },

  {
    keys: ["what is gd&t"],
    text:
      "GD&T (Geometric Dimensioning and Tolerancing) is the engineering language used on drawings to precisely define the size, form, orientation, and location of features to ensure assemblies fit and function correctly.",
  },

  {
    keys: [
      "difference between dfm and dfa",
      "dfm vs dfa",
    ],
    text:
      "DFM (Design for Manufacturing) makes individual parts easier and cheaper to produce. DFA (Design for Assembly) makes the final assembly faster and simpler to put together. Madhu applies both rigorously.",
  },

  {
    keys: [
      "what is bom",
      "bom",
      "bill of materials",
    ],
    text:
      "A BOM (Bill of Materials) is the complete list of all parts, sub-assemblies, and materials needed to build a product. Madhu generates and maintains precise CAD BOMs with ECO/ECN traceability.",
  },

  {
    keys: [
      "why should we hire madhu",
      "why choose madhu",
      "what makes him unique",
      "key strengths",
    ],
    text:
      "Madhu bridges theory and manufacture — he doesn't just model in CAD; he calculates tolerances, runs thermal FEA, and designs for actual shop-floor manufacturability. Rare combination for a junior engineer.",
  },

  {
    keys: [
      "notice period",
      "immediate joiner",
    ],
    text:
      "Madhu's notice period is negotiable. He is open to discussing immediate or standard transition timelines for the right opportunity.",
  },

  {
    keys: [
      "expected salary",
      "ctc",
      "salary",
      "package",
      "compensation",
    ],
    action: 3,
    text:
      "Madhu is open to competitive industry standards based on role and responsibilities. Please contact him directly at vangurimadhu13@gmail.com to discuss CTC confidentially.",
  },

  {
    keys: [
      "open for remote",
      "open for relocation",
      "relocate",
      "bangalore",
      "pune",
      "chennai",
      "location preference",
    ],
    text:
      "Madhu is based in Hyderabad but willing to relocate to major tech/manufacturing hubs. Prefers onsite/hybrid for hardware work but open to remote CAD roles.",
  },

  {
    keys: [
      "project",
      "portfolio",
      "3d model",
      "cad work",
      "show projects",
      "open projects",
      "explore",
      "view projects",
    ],
    action: 1,
    text:
      "Opening Projects — Madhu has published interactive 3D CAD models of his real-world industrial designs. Highlights include the X-Ray Monoblock Assembly and Chest Stand.",
  },

  {
    keys: [
      "open contact",
      "how to contact",
      "contact details",
      "email",
    ],
    action: 3,
    text:
      "Opening Contact. You can email Madhu directly at vangurimadhu13@gmail.com or use the form below.",
  },

  {
    keys: [
      "resume",
      "cv",
      "download resume",
      "show resume",
      "open resume",
    ],
    action: 2,
    text:
      "Opening Résumé — you can view a summary of his experience or download the full PDF directly.",
  },

  {
    keys: [
      "linkedin",
      "github",
      "social links",
      "connect on linkedin",
    ],
    action: 3,
    text:
      "Connect with Madhu on LinkedIn at linkedin.com/in/madhuvanguri or reach out via the Contact form.",
  },

  {
    keys: [
      "looking for",
      "hiring",
      "searching",
      "need engineer",
      "available for work",
      "open to work",
      "recruit",
      "hire madhu",
    ],
    action: 3,
    text:
      "You're in the right place. Madhu is open to new mechanical design opportunities. Opening Contact so you can reach him directly.",
  },

  {
    keys: [
      "bye",
      "goodbye",
      "thanks",
      "thank you",
      "good bot",
      "done",
      "exit",
      "close",
      "cya",
      "good night",
    ],
    text:
      "Thank you for visiting! Reach Madhu anytime at vangurimadhu13@gmail.com or via LinkedIn. Have a great day.",
  },
];

/* ══════════════════════════════════════════
   SMART ENGINE
══════════════════════════════════════════ */

const ALIASES = {
  sw: "solidworks",
  solidwork: "solidworks",
  solidwroks: "solidworks",
  "solid works": "solidworks",
  gdt: "gd&t",
  "gd t": "gd&t",
  fea: "ansys",
  "finite element analysis": "ansys",
  cad: "solidworks",
  cv: "resume",
  recruiter: "hiring",
  recruitment: "hiring",
  "x ray": "x-ray",
  monoblock: "x-ray monoblock",
  tube: "x-ray tube",
  "chest stand": "x-ray chest stand",
  stackup: "tolerance stackup",
  "tolerance stack": "tolerance stackup",
};

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s.+#&/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeWithAliases = (value = "") => {
  let text = normalizeText(value);

  Object.entries(ALIASES)
    .sort((a, b) => b[0].length - a[0].length)
    .forEach(([from, to]) => {
      const escaped = from.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );

      text = text.replace(
        new RegExp(`\\b${escaped}\\b`, "g"),
        to
      );
    });

  return text;
};

const INTENTS = {
  projects: [
    "project",
    "projects",
    "portfolio",
    "3d model",
    "cad work",
    "design",
    "designs",
  ],

  solidworks: [
    "solidworks",
    "3d modeling",
    "part modeling",
    "assembly",
    "cad",
  ],

  gdt: [
    "gd&t",
    "geometric dimensioning",
    "geometric tolerancing",
    "datum",
    "position tolerance",
    "tolerance",
  ],

  fea: [
    "ansys",
    "fea",
    "finite element",
    "simulation",
    "structural analysis",
    "thermal analysis",
  ],

  dfm: [
    "dfm",
    "dfa",
    "manufacturing",
    "machining",
    "manufacturability",
    "sheet metal",
  ],

  experience: [
    "experience",
    "work experience",
    "background",
    "career",
    "professional",
  ],

  resume: [
    "resume",
    "cv",
    "qualification",
    "education",
  ],

  hiring: [
    "hire",
    "hiring",
    "recruit",
    "recruiter",
    "job",
    "jobs",
    "opportunity",
    "open to work",
  ],

  contact: [
    "contact",
    "email",
    "reach",
    "linkedin",
    "connect",
    "message",
  ],
};

const scoreIntent = (text, terms) => {
  let score = 0;

  terms.forEach((term) => {
    if (text === term) {
      score += 100;
    } else if (text.includes(term)) {
      score += term.includes(" ")
        ? 20
        : 10;
    }
  });

  return score;
};

const detectIntent = (text) =>
  Object.entries(INTENTS)
    .map(([intent, terms]) => ({
      intent,
      score: scoreIntent(text, terms),
    }))
    .filter((item) => item.score > 0)
    .sort(
      (a, b) => b.score - a.score
    )[0]?.intent || null;

const getProject = (text) => {
  const checks = [
    {
      names: [
        "x-ray monoblock",
        "monoblock",
      ],
      project: PROJECTS[0],
    },
    {
      names: [
        "x-ray chest stand",
        "chest stand",
      ],
      project: PROJECTS[1],
    },
    {
      names: [
        "x-ray tube",
        "tube assembly",
      ],
      project: PROJECTS[2],
    },
    {
      names: [
        "robotic stretcher",
        "stretcher",
      ],
      project: PROJECTS[3],
    },
    {
      names: [
        "precision lathe",
        "lathe",
      ],
      project: PROJECTS[4],
    },
  ];

  for (const item of checks) {
    if (
      item.names.some((name) =>
        text.includes(name)
      )
    ) {
      return item.project;
    }
  }

  return null;
};

const findKnowledgeMatch = (text) => {
  let best = null;
  let bestScore = 0;

  for (const item of KNOWLEDGE_BASE) {
    let score = 0;

    for (const key of item.keys) {
      const normalizedKey =
        normalizeWithAliases(key);

      if (text === normalizedKey) {
        score = Math.max(score, 100);
      } else if (
        text.includes(normalizedKey)
      ) {
        score = Math.max(
          score,
          normalizedKey.includes(" ")
            ? 60
            : 40
        );
      }
    }

    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }

  return best;
};

const processInput = (
  input,
  history = []
) => {
  const text =
    normalizeWithAliases(input);

  if (!text) {
    return {
      text:
        "Ask me about Madhu's projects, SolidWorks, GD&T, ANSYS, DFM/DFA, experience, resume, hiring, or contact.",
    };
  }

  const project = getProject(text);

  if (project) {
    return {
      text:
        `**${project.title}**\n\n` +
        `${project.role}\n` +
        `${project.software}\n\n` +
        `${project.desc}`,
      action: 1,
    };
  }

  const intent = detectIntent(text);

  if (
    intent === "projects"
  ) {
    return {
      text:
        "Here are the main portfolio projects:\n\n" +
        "• **X-Ray Monoblock Assembly**\n" +
        "• **X-Ray Chest Stand**\n" +
        "• **X-Ray Tube Assembly**\n" +
        "• **IoT-Based Robotic Stretcher**\n" +
        "• **Precision Lathe Part**",
      action: 1,
    };
  }

  if (intent === "hiring") {
    return {
      text:
        "Madhu is open to suitable mechanical design opportunities. His strongest areas include SolidWorks, medical-device design, GD&T, DFM/DFA, thermal management, assemblies, and engineering validation.",
      action: 3,
    };
  }

  if (intent === "contact") {
    return {
      text:
        "You can contact Madhu directly through the Contact tab or email him at vangurimadhu13@gmail.com.",
      action: 3,
    };
  }

  if (intent === "resume") {
    return {
      text:
        "Opening the Résumé section. You can review Madhu's experience and download the full PDF.",
      action: 2,
    };
  }

  if (intent === "solidworks") {
    return {
      text:
        "Madhu uses **SolidWorks** for mechanical part modelling, assemblies, detailed design development, drawings, and production-oriented mechanical work.",
    };
  }

  if (intent === "gdt") {
    return {
      text:
        "Madhu applies **GD&T (ASME Y14.5)** for functional geometry, datums, orientation, location, tolerance stack-up, and manufacturing requirements.",
    };
  }

  if (intent === "fea") {
    return {
      text:
        "Madhu uses **ANSYS Workbench** for structural and thermal FEA, including engineering validation of components and thermal-management geometries.",
    };
  }

  if (intent === "dfm") {
    return {
      text:
        "Madhu applies **DFM/DFA** to make components easier to manufacture and assemblies easier to produce, inspect, and assemble.",
    };
  }

  const knowledge =
    findKnowledgeMatch(text);

  if (knowledge) {
    return {
      text: knowledge.text,
      action: knowledge.action,
    };
  }

  const previousUser = [...history]
    .reverse()
    .find(
      (message) =>
        message.type === "user"
    );

  if (previousUser) {
    const previousText =
      normalizeWithAliases(
        previousUser.text
      );

    const previousIntent =
      detectIntent(previousText);

    if (
      /^(more|tell me more|more info|more information|explain more|continue|what about this|and this)$/i.test(
        text
      )
    ) {
      if (
        previousIntent === "solidworks"
      ) {
        return {
          text:
            "For SolidWorks, Madhu's work covers detailed part modelling, assemblies, drawings, design development, and practical mechanical design.",
        };
      }

      if (
        previousIntent === "gdt"
      ) {
        return {
          text:
            "For GD&T, the key areas are datums, form, orientation, location, and controlling functional relationships between features.",
        };
      }

      if (
        previousIntent === "fea"
      ) {
        return {
          text:
            "For FEA, the focus is engineering validation through structural and thermal simulation.",
        };
      }

      if (
        previousIntent === "projects"
      ) {
        return {
          text:
            "Which project would you like to explore?\n\n" +
            "• X-Ray Monoblock\n" +
            "• X-Ray Chest Stand\n" +
            "• X-Ray Tube\n" +
            "• Robotic Stretcher\n" +
            "• Precision Lathe",
          action: 1,
        };
      }
    }
  }

  return {
    text:
      "I didn't quite catch that. Try asking about **Projects, SolidWorks, GD&T, ANSYS, DFM/DFA, Experience, Résumé, Hiring, or Contact**.",
  };
};

/* ══════════════════════════════════════════
   ENGINEERING CALCULATOR
══════════════════════════════════════════ */
const EngineeringTools = memo(() => {
  const [tool, setTool] = useState("torque");
  const [v1, setV1] = useState("");
  const [v2, setV2] = useState("");
  const [v3, setV3] = useState("");
  const [res, setRes] = useState(null);

  const calculate = useCallback(() => {
    const a = parseFloat(v1);
    const b = parseFloat(v2);
    const c = parseFloat(v3);

    if (tool === "torque") {
      setRes(
        isNaN(a) || isNaN(b)
          ? "⚠ Invalid input"
          : `Torque: ${(0.2 * a * b).toFixed(
              3
            )} Nm  (K = 0.2)`
      );
    } else if (tool === "beam") {
      if (
        isNaN(a) ||
        isNaN(b) ||
        isNaN(c)
      ) {
        setRes("⚠ Invalid input");
        return;
      }

      const defl =
        (a * Math.pow(b, 3)) /
        (3 *
          200e9 *
          (Math.pow(c, 4) / 12)) *
        1000;

      setRes(
        `Tip Deflection: ${defl.toFixed(
          4
        )} mm  (E = 200 GPa, square section)`
      );
    } else if (tool === "stack") {
      const vals = [a, b, c].filter(
        (n) => !isNaN(n)
      );

      if (vals.length < 2) {
        setRes(
          "⚠ Need at least 2 values"
        );
        return;
      }

      const rss = Math.sqrt(
        vals.reduce(
          (s, n) => s + n * n,
          0
        )
      );

      const wc = vals.reduce(
        (s, n) => s + Math.abs(n),
        0
      );

      setRes(
        `RSS Stackup: ±${rss.toFixed(
          4
        )} mm\nWorst-Case: ±${wc.toFixed(
          4
        )} mm`
      );
    } else {
      setRes(
        isNaN(a) || isNaN(b)
          ? "⚠ Invalid input"
          : `Factor of Safety: ${(a / b).toFixed(
              3
            )}  ${
              a / b >= 1.5
                ? "✓ Acceptable"
                : "⚠ Review design"
            }`
      );
    }
  }, [tool, v1, v2, v3]);

  const configs = useMemo(
    () => ({
      torque: {
        label:
          "Bolt Torque  T = K · D · F",
        fields: [
          {
            l: "Bolt Diameter (m)",
            v: v1,
            s: setV1,
          },
          {
            l: "Clamp Load (N)",
            v: v2,
            s: setV2,
          },
        ],
      },

      beam: {
        label: "Cantilever Deflection",
        fields: [
          {
            l: "Load P (N)",
            v: v1,
            s: setV1,
          },
          {
            l: "Length L (m)",
            v: v2,
            s: setV2,
          },
          {
            l: "Section side (m)",
            v: v3,
            s: setV3,
          },
        ],
      },

      stack: {
        label:
          "Tolerance Stackup  (RSS + WC)",
        fields: [
          {
            l: "Tol 1 (mm)",
            v: v1,
            s: setV1,
          },
          {
            l: "Tol 2 (mm)",
            v: v2,
            s: setV2,
          },
          {
            l: "Tol 3 (mm, opt.)",
            v: v3,
            s: setV3,
          },
        ],
      },

      safety: {
        label: "Factor of Safety",
        fields: [
          {
            l: "Material Strength (MPa)",
            v: v1,
            s: setV1,
          },
          {
            l: "Applied Stress (MPa)",
            v: v2,
            s: setV2,
          },
        ],
      },
    }),
    [v1, v2, v3]
  );

  const cfg = configs[tool];

  const inputStyle = useMemo(
    () => ({
      bgcolor:
        "rgba(255,255,255,0.7)",
      input: {
        color: G.ink,
        fontSize: "16px",
      },
      label: {
        color: G.muted,
        fontSize: "0.8rem",
      },
      "& .MuiFilledInput-root": {
        bgcolor:
          "rgba(255,255,255,0.7)",
        "&:hover": {
          bgcolor:
            "rgba(255,255,255,0.9)",
        },
      },
    }),
    []
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          ...glassPanel(),
          p: 2.5,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "8%",
            right: "8%",
            height: 1,
            background:
              "linear-gradient(90deg,transparent,rgba(255,255,255,0.9),transparent)",
            pointerEvents: "none",
          }}
        />

        <Typography
          sx={{
            fontFamily: G.mono,
            fontSize: "0.65rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: G.muted,
            mb: 2,
          }}
        >
          Engineering Calculators
        </Typography>

        <FormControl
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          <InputLabel
            id="calc-select-label"
            sx={{
              color: G.muted,
              fontSize: "0.8rem",
            }}
          >
            Select Calculator
          </InputLabel>

          <Select
            labelId="calc-select-label"
            value={tool}
            onChange={(e) => {
              setTool(e.target.value);
              setRes(null);
              setV1("");
              setV2("");
              setV3("");
            }}
            sx={{
              color: G.ink,
              bgcolor:
                "rgba(255,255,255,0.7)",
              "& .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: G.border,
                },
            }}
          >
            <MenuItem value="torque">
              Bolt Torque (T = K · D · F)
            </MenuItem>

            <MenuItem value="beam">
              Cantilever Beam Deflection
            </MenuItem>

            <MenuItem value="stack">
              Tolerance Stackup (RSS + WC)
            </MenuItem>

            <MenuItem value="safety">
              Factor of Safety
            </MenuItem>
          </Select>
        </FormControl>

        <Typography
          aria-live="polite"
          sx={{
            fontFamily: G.mono,
            fontSize: "0.58rem",
            color: G.muted,
            display: "block",
            mb: 1.5,
            letterSpacing: "0.06em",
          }}
        >
          {cfg.label}
        </Typography>

        <Grid container spacing={1.5}>
          {cfg.fields.map((f, i) => (
            <Grid
              item
              xs={6}
              key={i}
            >
              <TextField
                label={f.l}
                variant="filled"
                fullWidth
                size="small"
                value={f.v}
                onChange={(e) =>
                  f.s(e.target.value)
                }
                sx={inputStyle}
              />
            </Grid>
          ))}
        </Grid>

        <Button
          fullWidth
          variant="contained"
          onClick={calculate}
          sx={{
            mt: 2,
            bgcolor: G.primary,
            color: "#fff",
            fontFamily: G.mono,
            fontSize: "0.65rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            py: 1.2,
            borderRadius: "6px",
            "&:hover": {
              bgcolor: "#22406a",
            },
          }}
        >
          Calculate
        </Button>

        {res && (
          <Box
            role="alert"
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor:
                "rgba(26,48,80,0.05)",
              border: `1px solid ${G.border}`,
              borderRadius: "8px",
              fontFamily: G.mono,
              fontSize: "0.75rem",
              color: G.primary,
              whiteSpace: "pre-line",
              lineHeight: 1.7,
            }}
          >
            {res}
          </Box>
        )}
      </Box>
    </Box>
  );
});

EngineeringTools.displayName =
  "EngineeringTools";

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function ContactBot() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] =
    useState(false);
  const [suggestions, setSuggestions] =
    useState(SUGGESTION_SETS.default);

  const [formData, setFormData] =
    useState({
      name: "",
      email: "",
      msg: "",
    });

  const [formStatus, setFormStatus] =
    useState("idle");

  const [toastMsg, setToastMsg] =
    useState(null);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme.breakpoints.down("md")
  );
  const timersRef = useRef(new Set());

  /* Cleanup timers */
  useEffect(() => {
    const timers = timersRef.current;

    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const safeTimeout = useCallback(
    (cb, delay) => {
      const id = setTimeout(() => {
        timersRef.current.delete(id);
        cb();
      }, delay);

      timersRef.current.add(id);

      return id;
    },
    []
  );

  /* Greeting on open */
  useEffect(() => {
    if (
      open &&
      history.length === 0
    ) {
      safeTimeout(() => {
        setHistory([
          {
            type: "bot",
            text:
              "Hello! I'm Madhu's portfolio assistant.\n\nAsk me anything about his engineering experience, CAD projects, or availability — or try one of the suggestions below.",
            timestamp: new Date(),
          },
        ]);
      }, 350);
    }
  }, [
    open,
    history.length,
    safeTimeout,
  ]);

  /* Auto scroll */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [history, isThinking]);

  /* Send message */
  const handleSend = useCallback(
    (override = null) => {
      const raw =
        override !== null
          ? override
          : input;

      const txt =
        String(raw || "").trim();

      if (!txt || isThinking) return;

      setInput("");

      const userMessage = {
        type: "user",
        text: txt,
        timestamp: new Date(),
      };

      setHistory((prev) => [
        ...prev,
        userMessage,
      ]);

      setIsThinking(true);

      safeTimeout(() => {
        const currentHistory = [
          ...history,
          userMessage,
        ];

        const res = processInput(
          txt,
          currentHistory
        );

        setHistory((prev) => [
          ...prev,
          {
            type: "bot",
            text: res.text,
            timestamp: new Date(),
          },
        ]);

        if (
          res.action !== undefined
        ) {
          setTab(res.action);
        }

        if (res.action === 1) {
          setSuggestions(
            SUGGESTION_SETS.after_project
          );
        } else if (
          res.action === 3
        ) {
          setSuggestions(
            SUGGESTION_SETS.after_contact
          );
        } else if (
          res.action === 2
        ) {
          setSuggestions(
            SUGGESTION_SETS.after_resume
          );
        } else if (
          /gd&t|fea|dfm|tolerance|stackup|ansys|solidworks/i.test(
            txt
          )
        ) {
          setSuggestions(
            SUGGESTION_SETS.technical
          );
        } else if (
          /hire|recruit|notice|salary|ctc|join/i.test(
            txt
          )
        ) {
          setSuggestions(
            SUGGESTION_SETS.recruiter
          );
        } else {
          setSuggestions(
            SUGGESTION_SETS.default
          );
        }

        setIsThinking(false);

        safeTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }, 550);
    },
    [
      input,
      isThinking,
      history,
      safeTimeout,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (
        e.key === "Enter" &&
        !e.shiftKey
      ) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const clearHistory = useCallback(
    () => {
      setHistory([
        {
          type: "bot",
          text:
            "Chat cleared. Ask me anything about Madhu's work.",
          timestamp: new Date(),
        },
      ]);

      setSuggestions(
        SUGGESTION_SETS.default
      );
    },
    []
  );

  /* Contact submit */
  const handleContactSubmit =
    useCallback(() => {
      if (
        !formData.name.trim() ||
        !formData.email.trim() ||
        !formData.msg.trim()
      ) {
        setToastMsg({
          type: "error",
          text: "Please fill all fields.",
        });

        return;
      }

      if (
        !/^\S+@\S+\.\S+$/.test(
          formData.email
        )
      ) {
        setToastMsg({
          type: "error",
          text:
            "Please enter a valid email address.",
        });

        return;
      }

      setFormStatus("sending");

      emailjs
        .send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          {
            to_name: "Madhu Vanguri",
            from_name: formData.name,
            from_email: formData.email,
            message: formData.msg,
          },
          EMAILJS_CONFIG.PUBLIC_KEY
        )
        .then(() => {
          setFormStatus("sent");

          setFormData({
            name: "",
            email: "",
            msg: "",
          });

          setToastMsg({
            type: "success",
            text:
              "Message sent — Madhu will reply soon.",
          });

          safeTimeout(
            () =>
              setFormStatus("idle"),
            4000
          );
        })
        .catch((err) => {
          console.error(err);

          setFormStatus("idle");

          setToastMsg({
            type: "error",
            text:
              "Failed to send. Please email directly.",
          });
        });
    }, [formData, safeTimeout]);

  /* File upload */
  const handleFileUpload =
    useCallback(
      (e) => {
        const file =
          e.target.files?.[0];

        if (!file) return;

        setHistory((prev) => [
          ...prev,
          {
            type: "user",
            text: `📎 Attached: ${file.name}`,
            timestamp: new Date(),
          },
        ]);

        setIsThinking(true);

        safeTimeout(() => {
          setHistory((prev) => [
            ...prev,
            {
              type: "bot",
              text:
                `File received: **${file.name}**\n\nDescribe the part or its key dimensions and I can offer DFM feedback, tolerance suggestions, or material recommendations — or contact Madhu directly via the Contact tab.`,
              timestamp: new Date(),
            },
          ]);

          setIsThinking(false);
        }, 1000);
      },
      [safeTimeout]
    );

  /* Shared input style */
  const fieldStyle = useMemo(
    () => ({
      "& .MuiFilledInput-root": {
        bgcolor:
          "rgba(255,255,255,0.7)",
        borderRadius: "8px",

        "&:hover": {
          bgcolor:
            "rgba(255,255,255,0.9)",
        },
      },

      input: {
        color: G.ink,
        fontSize: "16px",
      },

      "& textarea": {
        color: G.ink,
        fontSize: "16px",
      },

      label: {
        color: G.muted,
      },
    }),
    []
  );

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  return (
    <>
      {/* FAB */}
      {!open && (
        <Box
          role="button"
          aria-label="Open chat assistant"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) =>
            e.key === "Enter" &&
            setOpen(true)
          }
          sx={{
            position: "fixed",
            bottom: isMobile ? 76 : 28,
            right: 22,
            zIndex: 9999,
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: G.glassDeep,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter:
              "blur(20px)",
            border: `1px solid ${G.border}`,
            boxShadow:
              "0 8px 32px rgba(26,48,80,0.14), inset 0 1px 0 rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition:
              "transform 0.22s ease, box-shadow 0.22s ease",

            "&:hover": {
              transform: "scale(1.07)",
              boxShadow:
                "0 12px 40px rgba(26,48,80,0.2), inset 0 1px 0 rgba(255,255,255,0.9)",
            },
          }}
        >
          <svg
            aria-hidden="true"
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
          >
            <circle
              cx="13"
              cy="13"
              r="11"
              stroke="#1a3050"
              strokeWidth="0.75"
              strokeOpacity="0.3"
            />

            <path
              d="M13 2 A11 11 0 0 1 24 13"
              stroke="#1a3050"
              strokeWidth="1"
              strokeOpacity="0.6"
              strokeLinecap="round"
            />

            <path
              d="M13 24 A11 11 0 0 1 2 13"
              stroke="#1a3050"
              strokeWidth="1"
              strokeOpacity="0.6"
              strokeLinecap="round"
            />

            <line
              x1="13"
              y1="2"
              x2="13"
              y2="5"
              stroke="#1a3050"
              strokeWidth="1.2"
              strokeOpacity="0.55"
            />

            <line
              x1="13"
              y1="21"
              x2="13"
              y2="24"
              stroke="#1a3050"
              strokeWidth="1.2"
              strokeOpacity="0.55"
            />

            <line
              x1="2"
              y1="13"
              x2="5"
              y2="13"
              stroke="#1a3050"
              strokeWidth="1.2"
              strokeOpacity="0.55"
            />

            <line
              x1="21"
              y1="13"
              x2="24"
              y2="13"
              stroke="#1a3050"
              strokeWidth="1.2"
              strokeOpacity="0.55"
            />

            <circle
              cx="13"
              cy="13"
              r="2.5"
              fill="#457b9d"
              fillOpacity="0.85"
            />
          </svg>
        </Box>
      )}

      {/* DIALOG */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={isMobile}
        TransitionComponent={Transition}
        maxWidth="md"
        aria-labelledby="bot-dialog-title"
        PaperProps={{
          sx: {
            maxWidth: "860px",
            width: isMobile
              ? "100%"
              : "88vw",
            height: isMobile
              ? "100dvh"
              : "86vh",
            maxHeight: isMobile
              ? "100dvh"
              : "820px",
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile
              ? 0
              : "16px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            background:
              "rgba(244, 243, 239, 0.82)",
            backdropFilter: "blur(40px)",
            WebkitBackdropFilter:
              "blur(40px)",
            border: isMobile
              ? "none"
              : `1px solid ${G.border}`,
            boxShadow:
              "0 32px 80px rgba(26,48,80,0.18), 0 0 0 1px rgba(255,255,255,0.5)",
          },
        }}
      >
        {/* Background grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            backgroundImage:
              "linear-gradient(rgba(26,48,80,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(26,48,80,0.04) 1px,transparent 1px)",
            backgroundSize: "28px 28px",
            WebkitMaskImage:
              "radial-gradient(ellipse 90% 90% at 50% 50%, black 40%, transparent 100%)",
            maskImage:
              "radial-gradient(ellipse 90% 90% at 50% 50%, black 40%, transparent 100%)",
          }}
        />

        {/* HEADER */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            flexShrink: 0,
            px: {
              xs: 1.75,
              sm: 2.5,
            },
            py: 1.5,
            pt: {
              xs: "max(14px, env(safe-area-inset-top))",
              sm: 1.5,
            },
            background: G.glassDeep,
            backdropFilter:
              "blur(20px)",
            WebkitBackdropFilter:
              "blur(20px)",
            borderBottom:
              `1px solid ${G.border}`,
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",

            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "6%",
              right: "6%",
              height: 1,
              background:
                "linear-gradient(90deg,transparent,rgba(255,255,255,0.95),transparent)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                position: "relative",
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: {
                    xs: 36,
                    sm: 42,
                  },
                  height: {
                    xs: 36,
                    sm: 42,
                  },
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #1a3050, #457b9d)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "center",
                  boxShadow:
                    "0 2px 12px rgba(26,48,80,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
                  fontFamily: G.display,
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                  },
                  fontWeight: 700,
                  color: "white",
                }}
              >
                M
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 1,
                  right: 1,
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: "#2d9e6b",
                  border:
                    "2px solid rgba(244,243,239,0.9)",
                  boxShadow:
                    "0 0 6px rgba(45,158,107,0.5)",
                }}
              />
            </Box>

            <Box>
              <Typography
                id="bot-dialog-title"
                sx={{
                  fontFamily: G.display,
                  fontWeight: 600,
                  fontSize: {
                    xs: "0.95rem",
                    sm: "1.05rem",
                  },
                  color: G.primary,
                  lineHeight: 1.1,
                }}
              >
                Madhu Vanguri
              </Typography>

              <Typography
                sx={{
                  fontFamily: G.mono,
                  fontSize: "0.58rem",
                  color: isThinking
                    ? G.steel
                    : "#2d6e50",
                  letterSpacing:
                    "0.1em",
                  textTransform:
                    "uppercase",
                }}
              >
                {isThinking
                  ? "Thinking…"
                  : "● Available"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            <Tooltip title="Clear chat">
              <IconButton
                aria-label="Clear chat history"
                onClick={clearHistory}
                size="small"
                sx={{
                  color: G.muted,
                  "&:hover": {
                    color: G.primary,
                  },
                }}
              >
                <Icons.Clear color="inherit" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Close">
              <IconButton
                aria-label="Close dialog"
                onClick={() =>
                  setOpen(false)
                }
                size="small"
                sx={{
                  color: G.muted,
                  "&:hover": {
                    color: G.primary,
                  },
                }}
              >
                <Icons.Close color="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {isThinking && (
          <LinearProgress
            aria-hidden="true"
            sx={{
              height: 1.5,
              flexShrink: 0,
              bgcolor: "transparent",
              "& .MuiLinearProgress-bar":
                {
                  bgcolor: G.steel,
                },
            }}
          />
        )}

        {/* TABS */}
        <Tabs
          aria-label="Assistant Navigation Tabs"
          value={tab}
          onChange={(_, v) =>
            setTab(v)
          }
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            zIndex: 2,
            position: "relative",
            flexShrink: 0,
            borderBottom:
              `1px solid ${G.border}`,
            background: G.glassDeep,
            backdropFilter:
              "blur(20px)",
            WebkitBackdropFilter:
              "blur(20px)",

            "& .MuiTab-root": {
              fontFamily: G.mono,
              fontSize: "0.62rem",
              letterSpacing: "0.1em",
              textTransform:
                "uppercase",
              color: G.muted,
              minWidth: {
                xs: 58,
                sm: 76,
              },
              py: 1.5,
            },

            "& .Mui-selected": {
              color: `${G.primary} !important`,
            },

            "& .MuiTabs-indicator": {
              bgcolor: G.primary,
              height: 1.5,
            },
          }}
        >
          <Tab label="Chat" />
          <Tab label="Projects" />
          <Tab label="Résumé" />
          <Tab label="Contact" />
          <Tab label="Tools" />
        </Tabs>

        {/* CONTENT */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            position: "relative",
            zIndex: 1,

            "&::-webkit-scrollbar": {
              width: 4,
            },

            "&::-webkit-scrollbar-thumb":
              {
                bgcolor:
                  "rgba(26,48,80,0.18)",
                borderRadius: 2,
              },

            "&::-webkit-scrollbar-track":
              {
                bgcolor: "transparent",
              },
          }}
        >
          {/* TAB 0: CHAT */}
          {tab === 0 && (
            <Box
              role="log"
              aria-live="polite"
              sx={{
                p: 2,
                display: "flex",
                flexDirection:
                  "column",
                gap: 1.5,
                pb: 2,
                minHeight: "100%",
              }}
            >
              {history.map(
                (msg, i) => (
                  <Fade
                    in
                    key={i}
                    timeout={280}
                  >
                    <Box
                      sx={{
                        alignSelf:
                          msg.type ===
                          "user"
                            ? "flex-end"
                            : "flex-start",
                        maxWidth: {
                          xs: "88%",
                          sm: "75%",
                        },
                        display: "flex",
                        flexDirection:
                          "column",
                        alignItems:
                          msg.type ===
                          "user"
                            ? "flex-end"
                            : "flex-start",
                        gap: 0.4,
                      }}
                    >
                      {msg.type ===
                        "bot" && (
                        <Typography
                          sx={{
                            fontFamily:
                              G.mono,
                            fontSize:
                              "0.55rem",
                            color:
                              G.muted,
                            ml: 0.5,
                            letterSpacing:
                              "0.08em",
                            textTransform:
                              "uppercase",
                          }}
                        >
                          Madhu's
                          Assistant
                        </Typography>
                      )}

                      <Box
                        sx={
                          msg.type ===
                          "user"
                            ? {
                                p: "10px 14px",
                                borderRadius:
                                  "14px 14px 4px 14px",
                                background:
                                  `linear-gradient(135deg, ${G.primary}, #22406a)`,
                                color:
                                  "white",
                                boxShadow:
                                  "0 4px 16px rgba(26,48,80,0.25)",
                              }
                            : {
                                p: "10px 14px",
                                borderRadius:
                                  "14px 14px 14px 4px",
                                background:
                                  G.glass,
                                backdropFilter:
                                  "blur(16px)",
                                WebkitBackdropFilter:
                                  "blur(16px)",
                                border:
                                  `1px solid ${G.border}`,
                                boxShadow:
                                  "0 2px 12px rgba(26,48,80,0.07), inset 0 1px 0 rgba(255,255,255,0.8)",
                                position:
                                  "relative",

                                "&::before":
                                  {
                                    content:
                                      '""',
                                    position:
                                      "absolute",
                                    top: 0,
                                    left: "6%",
                                    right: "6%",
                                    height: 1,
                                    background:
                                      "linear-gradient(90deg,transparent,rgba(255,255,255,0.8),transparent)",
                                  },
                              }
                        }
                      >
                        {msg.type ===
                        "user" ? (
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight:
                                1.65,
                              fontSize:
                                "0.84rem",
                            }}
                          >
                            {msg.text}
                          </Typography>
                        ) : (
                          <MessageText
                            text={
                              msg.text
                            }
                          />
                        )}
                      </Box>

                      <Typography
                        sx={{
                          fontFamily:
                            G.mono,
                          fontSize:
                            "0.55rem",
                          color:
                            G.muted,
                          mx: 0.5,
                        }}
                      >
                        {msg.timestamp?.toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </Typography>
                    </Box>
                  </Fade>
                )
              )}

              {isThinking && (
                <Fade
                  in
                  timeout={200}
                >
                  <Box
                    sx={{
                      alignSelf:
                        "flex-start",
                      background:
                        G.glass,
                      backdropFilter:
                        "blur(16px)",
                      WebkitBackdropFilter:
                        "blur(16px)",
                      border:
                        `1px solid ${G.border}`,
                      borderRadius:
                        "14px 14px 14px 4px",
                      boxShadow:
                        "0 2px 8px rgba(26,48,80,0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
                    }}
                  >
                    <TypingDots />
                  </Box>
                </Fade>
              )}

              <div ref={bottomRef} />
            </Box>
          )}

          {/* TAB 1: PROJECTS */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              <Grid
                container
                spacing={1.5}
              >
                {PROJECTS.map(
                  (p) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={p.id}
                    >
                      <Box
                        sx={{
                          ...glassPanel(
                            {
                              p: 0,
                              borderRadius:
                                "12px",
                            }
                          ),
                          height:
                            "100%",
                          display:
                            "flex",
                          flexDirection:
                            "column",
                          transition:
                            "transform 0.22s ease, box-shadow 0.22s ease",

                          "&:hover": {
                            transform:
                              "translateY(-3px)",
                            boxShadow:
                              "0 10px 40px rgba(26,48,80,0.14)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position:
                              "absolute",
                            top: 0,
                            left: "8%",
                            right: "8%",
                            height: 1,
                            background:
                              "linear-gradient(90deg,transparent,rgba(255,255,255,0.9),transparent)",
                            zIndex: 1,
                          }}
                        />

                        <Box
                          sx={{
                            p: 2,
                            flex: 1,
                            display:
                              "flex",
                            flexDirection:
                              "column",
                          }}
                        >
                          <Box
                            sx={{
                              display:
                                "flex",
                              justifyContent:
                                "space-between",
                              alignItems:
                                "center",
                              mb: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                fontFamily:
                                  G.mono,
                                fontSize:
                                  "0.55rem",
                                letterSpacing:
                                  "0.1em",
                                textTransform:
                                  "uppercase",
                                color:
                                  G.muted,
                                bgcolor:
                                  "rgba(26,48,80,0.06)",
                                border:
                                  `1px solid ${G.border}`,
                                borderRadius:
                                  "4px",
                                px: 1,
                                py: 0.4,
                              }}
                            >
                              {
                                p.category
                              }
                            </Box>

                            <Typography
                              sx={{
                                fontFamily:
                                  G.mono,
                                fontSize:
                                  "0.55rem",
                                color:
                                  G.muted,
                                letterSpacing:
                                  "0.06em",
                              }}
                            >
                              {p.role}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              fontFamily:
                                G.mono,
                              fontSize:
                                "0.8rem",
                              fontWeight: 600,
                              color:
                                G.primary,
                              mb: 0.4,
                              lineHeight:
                                1.3,
                            }}
                          >
                            {p.title}
                          </Typography>

                          <Typography
                            sx={{
                              fontFamily:
                                G.mono,
                              fontSize:
                                "0.58rem",
                              color:
                                G.steel,
                              mb: 1,
                              letterSpacing:
                                "0.06em",
                            }}
                          >
                            {p.software}
                          </Typography>

                          <Box
                            sx={{
                              height: 1,
                              background:
                                `linear-gradient(90deg, transparent, ${G.border} 30%, ${G.border} 70%, transparent)`,
                              mb: 1.25,
                            }}
                          />

                          <Typography
                            sx={{
                              fontSize:
                                "0.8rem",
                              color:
                                G.ink2,
                              lineHeight:
                                1.65,
                              flexGrow: 1,
                              mb: 1.75,
                              fontFamily:
                                G.body,
                            }}
                          >
                            {p.desc}
                          </Typography>

                          <Box
                            sx={{
                              display:
                                "flex",
                              gap: 0.75,
                            }}
                          >
                            {p.link3D && (
                              <Button
                                variant="contained"
                                size="small"
                                href={
                                  p.link3D
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  bgcolor:
                                    G.primary,
                                  color:
                                    "white",
                                  fontFamily:
                                    G.mono,
                                  fontSize:
                                    "0.58rem",
                                  letterSpacing:
                                    "0.08em",
                                  gap: 0.5,
                                  py: 0.8,
                                  px: 1.5,
                                  textTransform:
                                    "uppercase",

                                  "&:hover":
                                    {
                                      bgcolor:
                                        "#22406a",
                                    },
                                }}
                              >
                                <Icons.Cube color="white" />
                                3D View
                              </Button>
                            )}

                            {p.link2D && (
                              <Button
                                variant="outlined"
                                size="small"
                                href={
                                  p.link2D
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color:
                                    G.primary,
                                  borderColor:
                                    G.border,
                                  fontFamily:
                                    G.mono,
                                  fontSize:
                                    "0.58rem",
                                  letterSpacing:
                                    "0.08em",
                                  gap: 0.5,
                                  py: 0.8,
                                  px: 1.5,
                                  textTransform:
                                    "uppercase",
                                  bgcolor:
                                    "rgba(255,255,255,0.6)",

                                  "&:hover":
                                    {
                                      borderColor:
                                        "rgba(26,48,80,0.3)",
                                      bgcolor:
                                        "rgba(255,255,255,0.9)",
                                    },
                                }}
                              >
                                <Icons.Draft color={G.primary} />
                                2D Layout
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )
                )}
              </Grid>
            </Box>
          )}

          {/* TAB 2: RÉSUMÉ */}
          {tab === 2 && (
            <Box
              sx={{
                p: 2,
                maxWidth: 540,
                mx: "auto",
              }}
            >
              <Box
                sx={{
                  ...glassPanel({
                    p: 2.5,
                  }),
                }}
              >
                <Box
                  sx={{
                    position:
                      "absolute",
                    top: 0,
                    left: "8%",
                    right: "8%",
                    height: 1,
                    background:
                      "linear-gradient(90deg,transparent,rgba(255,255,255,0.9),transparent)",
                  }}
                />

                <Box
                  sx={{
                    display:
                      "flex",
                    justifyContent:
                      "space-between",
                    alignItems:
                      "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily:
                          G.display,
                        fontSize:
                          "1.6rem",
                        fontWeight: 700,
                        color:
                          G.primary,
                        lineHeight:
                          1,
                        mb: 0.3,
                      }}
                    >
                      Madhu Vanguri
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily:
                          G.mono,
                        fontSize:
                          "0.6rem",
                        color:
                          G.muted,
                        letterSpacing:
                          "0.1em",
                        textTransform:
                          "uppercase",
                      }}
                    >
                      Mechanical
                      Design
                      Engineer ·
                      Hyderabad,
                      India
                    </Typography>
                  </Box>

                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius:
                        "50%",
                      background:
                        "linear-gradient(135deg, #1a3050, #457b9d)",
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      fontFamily:
                        G.display,
                      fontWeight: 700,
                      fontSize:
                        "1.1rem",
                      color:
                        "white",
                      boxShadow:
                        "0 2px 12px rgba(26,48,80,0.2)",
                    }}
                  >
                    M
                  </Box>
                </Box>

                <Box
                  sx={{
                    height: 1,
                    background:
                      `linear-gradient(90deg, transparent, ${G.border} 20%, ${G.border} 80%, transparent)`,
                    mb: 2,
                  }}
                />

                <Typography
                  sx={{
                    fontFamily:
                      G.mono,
                    fontSize:
                      "0.55rem",
                    letterSpacing:
                      "0.16em",
                    textTransform:
                      "uppercase",
                    color:
                      G.muted,
                    mb: 1,
                  }}
                >
                  Core Skills
                </Typography>

                <Box
                  sx={{
                    display:
                      "flex",
                    flexWrap:
                      "wrap",
                    gap: 0.6,
                    mb: 2.5,
                  }}
                >
                  {[
                    "SolidWorks",
                    "ANSYS FEA",
                    "GD&T (ASME Y14.5)",
                    "DFM/DFA",
                    "AutoCAD",
                    "CATIA V5",
                    "Tolerance Stack-Up",
                    "Sheet Metal",
                    "BOM / ECO",
                    "Thermal Design",
                  ].map((s) => (
                    <Box
                      key={s}
                      sx={{
                        fontFamily:
                          G.mono,
                        fontSize:
                          "0.55rem",
                        letterSpacing:
                          "0.08em",
                        textTransform:
                          "uppercase",
                        color:
                          G.primary,
                        bgcolor:
                          "rgba(26,48,80,0.06)",
                        border:
                          `1px solid ${G.border}`,
                        borderRadius:
                          "4px",
                        px: 1,
                        py: 0.4,
                      }}
                    >
                      {s}
                    </Box>
                  ))}
                </Box>

                <Typography
                  sx={{
                    fontFamily:
                      G.mono,
                    fontSize:
                      "0.55rem",
                    letterSpacing:
                      "0.16em",
                    textTransform:
                      "uppercase",
                    color:
                      G.muted,
                    mb: 1,
                  }}
                >
                  Experience
                </Typography>

                {RESUME_DATA.experience.map(
                  (e, i) => (
                    <Box
                      key={i}
                      sx={{
                        mt:
                          i > 0
                            ? 1.5
                            : 0,
                        pl: 1.5,
                        borderLeft:
                          "2px solid rgba(69,123,157,0.35)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily:
                            G.mono,
                          fontSize:
                            "0.75rem",
                          fontWeight: 600,
                          color:
                            G.primary,
                        }}
                      >
                        {e.role}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily:
                            G.mono,
                          fontSize:
                            "0.65rem",
                          color:
                            G.steel,
                        }}
                      >
                        {e.company}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily:
                            G.mono,
                          fontSize:
                            "0.58rem",
                          color:
                            G.muted,
                          mb: 0.5,
                        }}
                      >
                        {e.date}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily:
                            G.body,
                          fontSize:
                            "0.78rem",
                          color:
                            G.ink2,
                          lineHeight:
                            1.55,
                        }}
                      >
                        {e.desc}
                      </Typography>
                    </Box>
                  )
                )}

                <Box
                  sx={{
                    height: 1,
                    background:
                      `linear-gradient(90deg, transparent, ${G.border} 20%, ${G.border} 80%, transparent)`,
                    my: 2,
                  }}
                />

                <Typography
                  sx={{
                    fontFamily:
                      G.mono,
                    fontSize:
                      "0.55rem",
                    letterSpacing:
                      "0.16em",
                    textTransform:
                      "uppercase",
                    color:
                      G.muted,
                    mb: 1,
                  }}
                >
                  Education
                </Typography>

                <Box
                  sx={{
                    pl: 1.5,
                    borderLeft:
                      "2px solid rgba(69,123,157,0.35)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.75rem",
                      fontWeight: 600,
                      color:
                        G.primary,
                    }}
                  >
                    B.E. Mechanical
                    Engineering
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.65rem",
                      color:
                        G.steel,
                    }}
                  >
                    TKR College of
                    Engineering &
                    Technology
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.58rem",
                      color:
                        G.muted,
                    }}
                  >
                    2020 – 2024
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                href="/Madhu_Vanguri.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mt: 1.5,
                  bgcolor: G.primary,
                  color: "white",
                  fontFamily: G.mono,
                  fontSize: "0.65rem",
                  letterSpacing: "0.12em",
                  textTransform:
                    "uppercase",
                  py: 1.3,
                  borderRadius: "8px",

                  "&:hover": {
                    bgcolor: "#22406a",
                  },
                }}
              >
                Download Full PDF
                Résumé
              </Button>
            </Box>
          )}

          {/* TAB 3: CONTACT */}
          {tab === 3 && (
            <Box
              sx={{
                p: 2,
                maxWidth: 460,
                mx: "auto",
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  ...glassPanel({
                    p: 2.5,
                  }),
                }}
              >
                <Box
                  sx={{
                    position:
                      "absolute",
                    top: 0,
                    left: "8%",
                    right: "8%",
                    height: 1,
                    background:
                      "linear-gradient(90deg,transparent,rgba(255,255,255,0.9),transparent)",
                  }}
                />

                <Typography
                  sx={{
                    fontFamily:
                      G.display,
                    fontSize:
                      "1.5rem",
                    fontWeight: 600,
                    color:
                      G.primary,
                    mb: 0.4,
                  }}
                >
                  Get In Touch
                </Typography>

                <Typography
                  sx={{
                    fontFamily:
                      G.body,
                    fontSize:
                      "0.82rem",
                    color:
                      G.muted,
                    mb: 2.25,
                    lineHeight:
                      1.65,
                  }}
                >
                  Open to mechanical
                  design roles in
                  medical devices,
                  thermal packaging,
                  and precision
                  engineering.
                </Typography>

                <Box
                  sx={{
                    display:
                      "flex",
                    flexDirection:
                      "column",
                    gap: 1.5,
                  }}
                >
                  <TextField
                    label="Your Name"
                    variant="filled"
                    fullWidth
                    value={
                      formData.name
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target
                          .value,
                      })
                    }
                    sx={fieldStyle}
                  />

                  <TextField
                    label="Your Email"
                    variant="filled"
                    fullWidth
                    value={
                      formData.email
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        email:
                          e.target.value,
                      })
                    }
                    sx={fieldStyle}
                  />

                  <TextField
                    label="Message / Opportunity"
                    variant="filled"
                    multiline
                    rows={4}
                    fullWidth
                    value={
                      formData.msg
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        msg: e.target.value,
                      })
                    }
                    sx={fieldStyle}
                  />

                  <Button
                    variant="contained"
                    size="large"
                    onClick={
                      handleContactSubmit
                    }
                    disabled={
                      formStatus ===
                      "sending"
                    }
                    sx={{
                      bgcolor:
                        formStatus ===
                        "sent"
                          ? "#2d9e6b"
                          : G.primary,
                      color: "white",
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.65rem",
                      letterSpacing:
                        "0.12em",
                      textTransform:
                        "uppercase",
                      py: 1.3,
                      borderRadius:
                        "8px",

                      "&:hover": {
                        bgcolor:
                          formStatus ===
                          "sent"
                            ? "#2d9e6b"
                            : "#22406a",
                      },
                    }}
                  >
                    {formStatus ===
                    "sending"
                      ? "Sending…"
                      : formStatus ===
                        "sent"
                      ? "✓ Sent"
                      : "Send Message"}
                  </Button>
                </Box>

                <Box
                  sx={{
                    height: 1,
                    background:
                      `linear-gradient(90deg, transparent, ${G.border} 20%, ${G.border} 80%, transparent)`,
                    my: 2,
                  }}
                />

                <Box
                  sx={{
                    display:
                      "flex",
                    gap: 1,
                    justifyContent:
                      "center",
                    flexWrap:
                      "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    href={`mailto:${CONFIG.email}`}
                    sx={{
                      color:
                        G.primary,
                      borderColor:
                        G.border,
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.6rem",
                      letterSpacing:
                        "0.08em",
                      bgcolor:
                        "rgba(255,255,255,0.6)",

                      "&:hover": {
                        borderColor:
                          "rgba(26,48,80,0.28)",
                        bgcolor:
                          "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    {CONFIG.email}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    href="https://www.linkedin.com/in/madhuvanguri"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color:
                        "#2563a8",
                      borderColor:
                        "rgba(37,99,168,0.25)",
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.6rem",
                      letterSpacing:
                        "0.08em",
                      bgcolor:
                        "rgba(255,255,255,0.6)",

                      "&:hover": {
                        borderColor:
                          "rgba(37,99,168,0.45)",
                        bgcolor:
                          "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    LinkedIn
                  </Button>
                </Box>
              </Box>

              <Snackbar
                open={!!toastMsg}
                autoHideDuration={
                  4000
                }
                onClose={() =>
                  setToastMsg(null)
                }
              >
                <Alert
                  severity={
                    toastMsg?.type ||
                    "info"
                  }
                  sx={{
                    width: "100%",
                    fontFamily:
                      G.body,
                  }}
                >
                  {toastMsg?.text}
                </Alert>
              </Snackbar>
            </Box>
          )}

          {/* TAB 4: TOOLS */}
          {tab === 4 && (
            <EngineeringTools />
          )}
        </Box>

        {/* INPUT BAR */}
        {tab === 0 && (
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              flexShrink: 0,
              px: {
                xs: 1.5,
                sm: 2,
              },
              pt: 1.25,
              pb: {
                xs: "max(12px, env(safe-area-inset-bottom))",
                sm: 1.5,
              },
              background:
                G.glassDeep,
              backdropFilter:
                "blur(20px)",
              WebkitBackdropFilter:
                "blur(20px)",
              borderTop:
                `1px solid ${G.border}`,
            }}
          >
            {/* Suggestions */}
            <Box
              sx={{
                display:
                  "flex",
                gap: 0.7,
                mb: 1.25,
                overflowX:
                  "auto",
                pb: "2px",

                "&::-webkit-scrollbar":
                  {
                    height: 2,
                  },

                "&::-webkit-scrollbar-thumb":
                  {
                    bgcolor:
                      "rgba(26,48,80,0.15)",
                    borderRadius: 1,
                  },
              }}
            >
              {suggestions.map(
                (s) => (
                  <Box
                    key={s}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      !isThinking &&
                      handleSend(s)
                    }
                    onKeyDown={(e) =>
                      e.key ===
                        "Enter" &&
                      !isThinking &&
                      handleSend(s)
                    }
                    sx={{
                      flexShrink: 0,
                      fontFamily:
                        G.mono,
                      fontSize:
                        "0.58rem",
                      letterSpacing:
                        "0.06em",
                      color:
                        G.muted,
                      whiteSpace:
                        "nowrap",
                      cursor:
                        isThinking
                          ? "default"
                          : "pointer",
                      bgcolor:
                        "rgba(255,255,255,0.7)",
                      border:
                        `1px solid ${G.border}`,
                      borderRadius:
                        "20px",
                      px: 1.25,
                      py: 0.6,
                      transition:
                        "all 0.18s ease",
                      opacity:
                        isThinking
                          ? 0.45
                          : 1,

                      "&:hover":
                        isThinking
                          ? {}
                          : {
                              bgcolor:
                                "white",
                              borderColor:
                                "rgba(26,48,80,0.22)",
                              color:
                                G.primary,
                            },
                    }}
                  >
                    {s}
                  </Box>
                )
              )}
            </Box>

            {/* Input */}
            <Box
              sx={{
                display:
                  "flex",
                alignItems:
                  "flex-end",
                gap: 0.5,
                background:
                  "rgba(255,255,255,0.75)",
                backdropFilter:
                  "blur(12px)",
                WebkitBackdropFilter:
                  "blur(12px)",
                border:
                  `1px solid ${
                    isThinking
                      ? "rgba(69,123,157,0.35)"
                      : G.border
                  }`,
                borderRadius:
                  "12px",
                p: "6px 8px",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 8px rgba(26,48,80,0.06)",
                transition:
                  "border-color 0.25s ease",
              }}
            >
              <Tooltip title="Attach file">
                <IconButton
                  component="label"
                  aria-label="Attach file"
                  size="small"
                  sx={{
                    color:
                      G.muted,
                    flexShrink: 0,
                    mb: 0.3,

                    "&:hover": {
                      color:
                        G.primary,
                    },
                  }}
                >
                  <Icons.Attach color="inherit" />

                  <input
                    type="file"
                    hidden
                    onChange={
                      handleFileUpload
                    }
                    accept="image/*,.pdf"
                  />
                </IconButton>
              </Tooltip>

              <InputBase
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                placeholder={
                  isThinking
                    ? "Thinking…"
                    : "Ask anything about Madhu's work…"
                }
                value={input}
                onChange={(e) =>
                  setInput(
                    e.target.value
                  )
                }
                onKeyDown={
                  handleKeyDown
                }
                disabled={
                  isThinking
                }
                inputProps={{
                  "aria-label":
                    "Chat input",
                }}
                sx={{
                  mx: 0.5,
                  color: G.ink,
                  fontSize: "16px",

                  "& textarea": {
                    color: G.ink,
                    fontSize:
                      "16px",
                    fontFamily:
                      G.body,
                  },
                }}
              />

              <Tooltip title="Send (Enter)">
                <span>
                  <IconButton
                    aria-label="Send message"
                    onClick={() =>
                      handleSend()
                    }
                    disabled={
                      isThinking ||
                      !input.trim()
                    }
                    size="small"
                    sx={{
                      flexShrink: 0,
                      mb: 0.3,
                      width: 32,
                      height: 32,
                      borderRadius:
                        "8px",
                      bgcolor:
                        input.trim() &&
                        !isThinking
                          ? G.primary
                          : "transparent",
                      color:
                        input.trim() &&
                        !isThinking
                          ? "white"
                          : G.muted,
                      transition:
                        "all 0.2s ease",

                      "&:not(:disabled):hover":
                        {
                          bgcolor:
                            "#22406a",
                        },

                      "&.Mui-disabled":
                        {
                          opacity:
                            0.4,
                        },
                    }}
                  >
                    {isThinking ? (
                      <CircularProgress
                        size={16}
                        sx={{
                          color:
                            G.steel,
                        }}
                      />
                    ) : (
                      <Icons.Send color="inherit" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Typography
              aria-hidden="true"
              sx={{
                fontFamily:
                  G.mono,
                fontSize:
                  "0.52rem",
                color:
                  G.muted,
                textAlign:
                  "center",
                mt: 0.75,
                letterSpacing:
                  "0.06em",
              }}
            >
              Shift + Enter for new
              line
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  );
}